import React, { useState, useEffect } from 'react';
import { times } from 'lodash';
import { DayOfWeek, AvailabilityRuleInput } from 'globalTypes';
import { CoachAvailabilityInterval } from 'coaching/CoachingConstants';
import { consolidateTimeIntervals } from 'coaching/lib/timeIntervals';
import Button from 'components/ButtonDeprecated';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import TimeInputRow from 'coaching/components/CoachAvailabilityView/TimeInputRow';
import useTimeIntervals from 'coaching/hooks/useTimeIntervals';

export type Props = {
  dayOfWeek: DayOfWeek;
  initialRules?: AvailabilityRuleInput[];
  maxSessions?: number;
  editingMaxSessions?: boolean;
  eventPrefix?: string;
  onChangeIntervals: (dayOfWeek: DayOfWeek, newIntervals: CoachAvailabilityInterval[]) => void;
  onChangeMaxSessions: (dayOfWeek: DayOfWeek, maxSessions?: number) => void;
};

// Maximum value for the maxSessions dropdown.
// Arbitrary number set by design (we don't restrict this in the backend)
const MAX_SESSIONS_LIMIT = 7;
const maxSessionsOptions = [
  { label: 'Any', value: 0 },
  ...times(MAX_SESSIONS_LIMIT, n => ({ label: (n + 1).toString(), value: n + 1 })),
];

export default function EditDayRow({
  dayOfWeek,
  initialRules,
  maxSessions,
  editingMaxSessions,
  eventPrefix,
  onChangeIntervals,
  onChangeMaxSessions,
}: Props) {
  const initialIntervals = initialRules?.map(ruleToInterval);
  const {
    intervals,
    addInterval,
    removeInterval,
    updateIntervalStartTime,
    updateIntervalEndTime,
    defaultInterval,
  } = useTimeIntervals(initialIntervals);
  const [maxSessionsNumber, setMaxSessionsNumber] = useState<number | undefined>(maxSessions);

  useEffect(() => {
    const mergedIntervals = consolidateTimeIntervals(intervals);
    onChangeIntervals(dayOfWeek, mergedIntervals);
  }, [intervals]);

  useEffect(() => {
    onChangeMaxSessions(dayOfWeek, maxSessionsNumber);
  }, [maxSessionsNumber]);

  function maybeTrackEvent(event: string) {
    if (eventPrefix) {
      EventTracking.trackKey(`${eventPrefix}.${event}`);
    }
  }

  const renderMaxSessionsInput = (maxSessions?: number) => {
    return (
      <MediaObject center gutter="xsmall">
        <Select
          data-test={`max-sessions-input-${dayOfWeek.toString()}`}
          searchable={false}
          inline
          placeholder="Any"
          options={maxSessionsOptions}
          value={maxSessions}
          onChange={(maxSessions: string) => {
            setMaxSessionsNumber(parseInt(maxSessions));
            maybeTrackEvent('add_max_daily_sessions.click');
          }}
        />
        <Text className="CoachingIsolatedView-maxSessionsPicker-text" color="graphite">
          sessions max
        </Text>
      </MediaObject>
    );
  };

  return (
    <React.Fragment>
      <Row size="xsmall">
        <MediaObject center figure="right">
          <Text weight="semibold">{dayOfWeek}</Text>
          <Button
            data-test="add-hours-action"
            weight="semibold"
            onClick={() => {
              addInterval(defaultInterval, intervals.length);
              maybeTrackEvent('add_hours.click');
            }}
            level="text"
            color="mascot">
            Add hours
          </Button>
        </MediaObject>
      </Row>
      {intervals.map((interval, index) => (
        <TimeInputRow
          key={`${interval.startTime}-${index}`}
          interval={interval}
          index={index}
          onRemoveInterval={removeInterval}
          onUpdateIntervalEndTime={updateIntervalEndTime}
          onUpdateIntervalStartTime={updateIntervalStartTime}
          eventPrefix={eventPrefix}
        />
      ))}
      {editingMaxSessions && intervals.length > 0 && renderMaxSessionsInput(maxSessions)}
      <Divider size="small" />
    </React.Fragment>
  );
}

function ruleToInterval(r: AvailabilityRuleInput) {
  return { startTime: r.startTime, endTime: r.endTime };
}
